import React, { useEffect } from 'react';
import {
  Grid,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { useStyles } from './style';
import jMoment from 'moment-jalaali';
import { HeaderSection, LoadingPage, PageContainer, Icons, PriceLabel } from 'components';
import { numberWithCommas, constBreadCrumbsTitle } from 'utility';
import copy from 'copy-to-clipboard';
import createToast from 'components/Toast/Toast';
import { useNavigate, useParams } from 'react-router-dom';
import { VOUCHER_TYPES, PERCENTAGE_VOUCHER_INDEXES } from '../constants/VoucherConsts';
import DiscountDetailServices from './services/DiscountDetailServices';
import useCustomHook from '../hooks';
import { useCurrencyUnit} from 'hooks';

const Constraints = () => {
  // -- STATE --//
  const { loading, setLoading, code, setCode } = useCustomHook();
  const voucherId = useParams(); // id web service (entity ID)
  const currencyText = useCurrencyUnit(false);
  const classes = useStyles();
  const navigate = useNavigate();
  useEffect(() => {
    if (voucherId) {
      getDiscountDetail(voucherId?.id);
    }
  }, [voucherId]);

  // -- API REQUESTS --//
  const getDiscountDetail = async (id) => {
    try {
      setLoading(true);
      const res = await DiscountDetailServices.getVourcher(id);
      setCode(res?.result[0] ?? null);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const copyItem = async (v) => {
    try {
      await copy(v);
      createToast('موفق', 'کد بن کپی شد.', 'success');
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <PageContainer
      title="اطلاعات بن تخفیف"
      onBackClick={() => navigate(-1)}
      breadcrumbs={constBreadCrumbsTitle.VOUCHER_DETAILS}
      className={classes.root}
    >
      <Grid container direction="row" alignItems="center" item>
        {loading ? <LoadingPage /> : null}
        {code && !loading ? (
          <Grid item xs={12}>
            <Grid container xs={12} md={9} className={classes.info} style={{ marginTop: -4 * 8 }}>
              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography>عنوان بن تخفیف</Typography>
                <Typography className={classes.title}>{code?.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography>وضعیت بن</Typography>
                <Typography className={classes.title}>
                  {code?.active ? 'فعال' : 'غیر فعال'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography>میزان استفاده شده</Typography>
                <Typography className={classes.title}>
                  {code?.usedAmount != null
                    ?  <PriceLabel amount={code.usedAmount} currency={currencyText} />
                    : '-'}
                </Typography>
              </Grid>
            </Grid>

            <Grid container xs={12} md={9} className={classes.info}>
              <Grid item xs={12}>
                <HeaderSection title="اطلاعات پایه" style={{ marginTop: 0 }} />
              </Grid>
              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography className={classes.detailTitle}>نوع بن صادر شده</Typography>
                <Typography className={classes.detailValue}>
                  {VOUCHER_TYPES[code?.type] ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography className={classes.detailTitle}>
                  {PERCENTAGE_VOUCHER_INDEXES.includes(code?.type) ? 'درصد تخفیف' : 'مبلغ تخفیف'}
                </Typography>
                <Typography className={classes.detailValue}>
                  {PERCENTAGE_VOUCHER_INDEXES.includes(code?.type)
                    ? numberWithCommas(code?.discountPercentage ?? '')
                    : code?.creditAmount
                      ? <PriceLabel amount={code.creditAmount} currency={currencyText} /> 
                      : 'بدون مبلغ'}
                </Typography>
              </Grid>
              {PERCENTAGE_VOUCHER_INDEXES.includes(code?.type) ? (
                <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                  <Typography className={classes.detailTitle}>سقف تخفیف</Typography>
                  <Typography className={classes.detailValue}>
                    {code?.creditAmount
                      ? <PriceLabel amount={code.creditAmount} currency={currencyText} />
                      : 'بدون سقف'}
                  </Typography>
                </Grid>
              ) : null}
              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography className={classes.detailTitle}>کد بن</Typography>
                <Typography className={classes.detailValue} style={{ fontFamily: 'Vazir' }}>
                  {code?.hash ? (
                    <>
                      {code?.hash}
                      <span className={classes.copyIcon} onClick={() => copyItem(code.hash)}>
                        <Icons icon='copy' color='#ccc' hover='#667585' size='20'  />
                      </span>
                    </>
                  ) : (
                    '-'
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography className={classes.detailTitle}>تاریخ شروع</Typography>
                <Typography className={classes.detailValue}>
                  {code?.effectiveDate
                    ? jMoment(new Date(parseInt(code.effectiveDate)))?.format(
                      'dddd, jD jMMMM jYYYY',
                    ) ?? '-'
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography className={classes.detailTitle}>تاریخ انقضا</Typography>
                <Typography className={classes.detailValue}>
                  {code?.expireDate
                    ? jMoment(code.expireDate)?.format('dddd, jD jMMMM jYYYY') ?? '-'
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <HeaderSection title="محدودیت‌ها" />
              </Grid>

              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography className={classes.detailTitle}>حداقل تعداد اقلام یک فاکتور</Typography>
                <Typography className={classes.detailValue}>
                  {code?.minInvoiceLineForApply ?? '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} lg={4} className={classes.detail}>
                <Typography className={classes.detailTitle}>حداقل مبلغ یک فاکتور</Typography>
                <Typography className={classes.detailValue}>
                  {code?.minInvoiceAmountForApply
                    ? <PriceLabel amount={code.minInvoiceAmountForApply} currency={currencyText} />
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} className={classes.detail}>
                <Typography className={classes.detailTitle}>محدودیت روی محل مصرف</Typography>
                <Typography className={classes.detailValue}>
                  {code?.guild ? code?.guild?.name : 'ندارد'}
                </Typography>
              </Grid>

              {/* Products Limits */}
              <Grid item xs={12} className={classes.detail}>
                <Typography className={classes.detailTitle}>قابل استفاده بر روی محصولات</Typography>

                {code?.productList?.length <= 0 ? (
                  <Typography className={classes.detailValue}>همه محصولات</Typography>
                ) : (
                  code?.productList?.map((v, k) => (
                    <Typography className={classes.detailValue}>
                      <span key={k}>{`${v?.id}. ${v?.name}`}</span>
                    </Typography>
                  ))
                )}
              </Grid>
              {/* Brokers Limit */}
              <Grid item xs={12} className={classes.detail}>
                <Typography className={classes.detailTitle}>کارگزارهای مجاز به استفاده</Typography>

                {code?.dealerBusinessList?.length <= 0 ? (
                  <Typography className={classes.detailValue}>همه کسب‌وکارهای کارگزار</Typography>
                ) : (
                  code?.dealerBusinessList?.map((v, k) => (
                    <Typography className={classes.detailValue}>
                      <span key={k}>{`${v?.id}. ${v?.name}`}</span>
                    </Typography>
                  ))
                )}
              </Grid>

              <Grid item xs={12}>
                <HeaderSection title="سوابق استفاده از بن تخفیف" />
              </Grid>

              <Grid item xs={12} md={6}>
                <TableContainer elevation={0} component={Paper} className={classes.table}>
                  <Table>
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell className={classes.tableCell}>میزان استفاده({currencyText})</TableCell>
                        <TableCell className={classes.tableCell}>تاریخ استفاده</TableCell>
                        <TableCell className={classes.tableCell}>وضعیت</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* List Data Map */}
                      {code.usageList?.length > 0 ? (
                        code.usageList.map((v, k) => (
                          <TableRow key={k}>
                            <TableCell className={classes.tableDataCell}>
                              {v?.usedAmount ? numberWithCommas(v.usedAmount) : 0}
                            </TableCell>
                            <TableCell className={classes.tableDataCell}>
                              {v?.consumDate
                                ? jMoment(v.consumDate).format('dddd, jD jMMMM jYYYY')
                                : '-'}
                            </TableCell>
                            <TableCell className={classes.tableDataCell}>
                              {v?.canceled ? 'لغو شده' : 'انجام شده'}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <Typography className={classes.noUsage}>استفاده نشده!</Typography>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    </PageContainer>
  );
};

export default Constraints;
